import { Helmet } from "react-helmet-async";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { supabaseReplica, shouldUseReplica } from "@/integrations/supabase/replica";
import { TrendingUp, BarChart3, Globe, Database, Calendar, Activity, MapPin, ExternalLink, Star, Shield } from "lucide-react";
import Highcharts from 'highcharts';
import BackgroundMediaComponent from "@/components/BackgroundMedia";
import HourlyIngestionTimeline from "@/components/admin/HourlyIngestionTimeline";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";

interface DailyAnalytics {
  snapshot_date: string;
  total_hospitals: number;
  hospitals_with_data: number;
  total_procedures: number;
  new_hospitals_today: number;
  new_procedures_today: number;
  usa_coverage_percentage: number;
  total_file_size_mb: number;
  hospitals_imported_today: string[];
}

interface StateStats {
  state: string;
  hospital_count: number;
  hospitals_with_data: number;
  estimated_total: number;
  completion_percentage: number;
  research_notes_count: number;
  priority: string;
}

const HospitalIngestionDashboard = () => {
  const hospitalGrowthRef = useRef<HTMLDivElement>(null);
  const procedureGrowthRef = useRef<HTMLDivElement>(null);
  const dailyImportsRef = useRef<HTMLDivElement>(null);
  const coverageRef = useRef<HTMLDivElement>(null);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAHP = brand.key === 'ahp';
  const isAHPDark = isAHP && isDark;

  const [mounted, setMounted] = useState(false);

  // Palette: AHP light → AHP dark → Luigi's (always dark)
  const palette = isAHPDark ? {
    // AHP Dark Mode — navy background, warm white text, gold/blue accents
    bg: 'bg-[#0a1628]',
    cardBg: 'bg-[#0d1e35] border-[#FAF9F6]/10',
    headerText: 'text-[#FAF9F6]',
    subText: 'text-[#FAF9F6]/60',
    mutedText: 'text-[#FAF9F6]/45',
    accent1: '#B22234',  // red
    accent2: '#5B9BD5',  // lighter blue for dark bg
    accent3: '#4ade80',  // bright green for dark bg
    accent4: '#C9A961',  // gold
    chartTitle: '#FAF9F6',
    chartSubtitle: 'rgba(250,249,246,0.55)',
    chartLabel: 'rgba(250,249,246,0.55)',
    chartGrid: 'rgba(250,249,246,0.1)',
    tooltipBg: '#0d1e35',
    tooltipBorder: '#5B9BD5',
    tooltipText: '#FAF9F6',
    badgeGood: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    badgeNeutral: 'bg-[#5B9BD5]/15 text-[#5B9BD5] border-[#5B9BD5]/30',
    stateGood: 'border-emerald-500/40 bg-emerald-500/10',
    stateMid: 'border-[#C9A961]/40 bg-[#C9A961]/10',
    stateLow: 'border-[#B22234]/40 bg-[#B22234]/10',
    featureBox: 'bg-[#5B9BD5]/5 border border-[#5B9BD5]/10',
    featureTitle: 'text-[#5B9BD5]',
    featureText: 'text-[#FAF9F6]/60',
  } : isAHP ? {
    // AHP Light Mode — warm white background, navy text, patriotic accents
    bg: 'bg-[#F7F4EF]',
    cardBg: 'bg-white/90 border-[#1a3a6b]/20 shadow-sm',
    headerText: 'text-[#1a3a6b]',
    subText: 'text-[#4a5568]',
    mutedText: 'text-[#718096]',
    accent1: '#B22234',  // red
    accent2: '#1a3a6b',  // navy
    accent3: '#2b5e49',  // forest green
    accent4: '#C9A961',  // gold
    chartTitle: '#1a3a6b',
    chartSubtitle: '#4a5568',
    chartLabel: '#4a5568',
    chartGrid: '#e2e8f0',
    tooltipBg: '#ffffff',
    tooltipBorder: '#1a3a6b',
    tooltipText: '#1a3a6b',
    badgeGood: 'bg-[#2b5e49]/10 text-[#2b5e49] border-[#2b5e49]/30',
    badgeNeutral: 'bg-[#1a3a6b]/10 text-[#1a3a6b] border-[#1a3a6b]/30',
    stateGood: 'border-[#2b5e49]/50 bg-[#2b5e49]/10',
    stateMid: 'border-[#C9A961]/50 bg-[#C9A961]/10',
    stateLow: 'border-[#B22234]/50 bg-[#B22234]/10',
    featureBox: 'bg-[#1a3a6b]/5 border border-[#1a3a6b]/10',
    featureTitle: 'text-[#1a3a6b]',
    featureText: 'text-[#4a5568]',
  } : {
    // Luigi's — matrix green on black (always dark)
    bg: 'bg-background',
    cardBg: 'bg-black/40 border-muted',
    headerText: 'text-matrix-green font-mono',
    subText: 'text-muted-foreground',
    mutedText: 'text-muted-foreground',
    accent1: '#10b981',
    accent2: '#3b82f6',
    accent3: '#8b5cf6',
    accent4: '#f59e0b',
    chartTitle: '#e2e8f0',
    chartSubtitle: '#94a3b8',
    chartLabel: '#94a3b8',
    chartGrid: '#374151',
    tooltipBg: '#1f2937',
    tooltipBorder: '#374151',
    tooltipText: '#e2e8f0',
    badgeGood: 'bg-green-500/20 text-green-400',
    badgeNeutral: '',
    stateGood: 'border-green-500/50 bg-green-500/10',
    stateMid: 'border-yellow-500/50 bg-yellow-500/10',
    stateLow: 'border-red-500/50 bg-red-500/10',
    featureBox: 'bg-blue-950/30',
    featureTitle: 'text-blue-100',
    featureText: 'text-blue-200',
  };

  // Fetch daily analytics data
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['daily-import-analytics'],
    queryFn: async () => {
      const replicaClient = shouldUseReplica('analytics') ? supabaseReplica : supabase;
      console.log('Using replica for ingestion dashboard:', shouldUseReplica('analytics'));
      const { data, error } = await replicaClient
        .from('daily_import_analytics')
        .select('snapshot_date,total_hospitals,hospitals_with_data,total_procedures,new_hospitals_today,new_procedures_today,usa_coverage_percentage,total_file_size_mb')
        .order('snapshot_date', { ascending: true });

      if (error) throw error;
      return data as DailyAnalytics[];
    },
    refetchInterval: 30000
  });

  // Fetch state-level statistics
  const { data: stateStats, isLoading: stateStatsLoading } = useQuery({
    queryKey: ['state-ingestion-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_state_ingestion_summary');

      if (error) throw error;
      return data as StateStats[];
    },
    refetchInterval: 60000
  });

  const latestData = analyticsData?.[analyticsData.length - 1];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!analyticsData || !mounted) return;

    const dates = analyticsData.map(d => d.snapshot_date);
    const hospitalCounts = analyticsData.map(d => d.hospitals_with_data);
    const procedureCounts = analyticsData.map(d => d.total_procedures);
    const dailyImports = analyticsData.map(d => d.new_hospitals_today);
    const coverageData = analyticsData.map(d => d.usa_coverage_percentage);

    const sharedChartConfig = {
      credits: { enabled: false } as any,
    };

    // Hospital Growth Chart
    if (hospitalGrowthRef.current) {
      Highcharts.chart(hospitalGrowthRef.current, {
        ...sharedChartConfig,
        chart: { type: 'area', backgroundColor: 'transparent', height: 300 },
        title: {
          text: 'Hospital Database Growth',
          style: { color: palette.chartTitle, fontSize: '18px', fontWeight: 'bold' }
        },
        subtitle: {
          text: 'Total hospitals with substantial pricing data (>100 procedures)',
          style: { color: palette.chartSubtitle }
        },
        xAxis: {
          categories: dates.map(d => new Date(d).toLocaleDateString()),
          labels: { style: { color: palette.chartLabel } },
          gridLineColor: palette.chartGrid
        },
        yAxis: {
          title: { text: 'Hospitals', style: { color: palette.chartLabel } },
          labels: { style: { color: palette.chartLabel } },
          gridLineColor: palette.chartGrid
        },
        series: [{
          name: 'Hospitals',
          data: hospitalCounts,
          color: palette.accent3,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, isAHPDark ? 'rgba(74, 222, 128, 0.7)' : isAHP ? 'rgba(43, 94, 73, 0.6)' : 'rgba(16, 185, 129, 0.8)'],
              [1, isAHPDark ? 'rgba(74, 222, 128, 0.05)' : isAHP ? 'rgba(43, 94, 73, 0.05)' : 'rgba(16, 185, 129, 0.1)']
            ]
          }
        }],
        legend: { enabled: false },
        tooltip: {
          backgroundColor: palette.tooltipBg,
          borderColor: palette.tooltipBorder,
          style: { color: palette.tooltipText },
          formatter: function() { return `<b>${this.x}</b><br/>Hospitals: <b>${this.y.toLocaleString()}</b>`; }
        },
        plotOptions: { area: { marker: { enabled: false }, lineWidth: 2 } }
      });
    }

    // Procedure Count Chart
    if (procedureGrowthRef.current) {
      Highcharts.chart(procedureGrowthRef.current, {
        ...sharedChartConfig,
        chart: { type: 'area', backgroundColor: 'transparent', height: 300 },
        title: {
          text: 'Medical Procedure Database Growth',
          style: { color: palette.chartTitle, fontSize: '18px', fontWeight: 'bold' }
        },
        subtitle: {
          text: 'Total medical procedures with pricing data across all hospitals',
          style: { color: palette.chartSubtitle }
        },
        xAxis: {
          categories: dates.map(d => new Date(d).toLocaleDateString()),
          labels: { style: { color: palette.chartLabel } },
          gridLineColor: palette.chartGrid
        },
        yAxis: {
          title: { text: 'Procedures (Millions)', style: { color: palette.chartLabel } },
          labels: {
            style: { color: palette.chartLabel },
            formatter: function() { return (this.value / 1000000).toFixed(1) + 'M'; }
          },
          gridLineColor: palette.chartGrid
        },
        series: [{
          name: 'Procedures',
          data: procedureCounts,
          color: palette.accent2,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, isAHPDark ? 'rgba(91, 155, 213, 0.7)' : isAHP ? 'rgba(26, 58, 107, 0.6)' : 'rgba(59, 130, 246, 0.8)'],
              [1, isAHPDark ? 'rgba(91, 155, 213, 0.05)' : isAHP ? 'rgba(26, 58, 107, 0.05)' : 'rgba(59, 130, 246, 0.1)']
            ]
          }
        }],
        legend: { enabled: false },
        tooltip: {
          backgroundColor: palette.tooltipBg,
          borderColor: palette.tooltipBorder,
          style: { color: palette.tooltipText },
          formatter: function() { return `<b>${this.x}</b><br/>Procedures: <b>${this.y.toLocaleString()}</b>`; }
        },
        plotOptions: { area: { marker: { enabled: false }, lineWidth: 2 } }
      });
    }

    // Daily Imports Chart
    if (dailyImportsRef.current) {
      Highcharts.chart(dailyImportsRef.current, {
        ...sharedChartConfig,
        chart: { type: 'column', backgroundColor: 'transparent', height: 300 },
        title: {
          text: 'Daily Hospital Import Activity',
          style: { color: palette.chartTitle, fontSize: '18px', fontWeight: 'bold' }
        },
        subtitle: {
          text: 'New hospitals added to the database each day',
          style: { color: palette.chartSubtitle }
        },
        xAxis: {
          categories: dates.map(d => new Date(d).toLocaleDateString()),
          labels: { style: { color: palette.chartLabel } },
          gridLineColor: palette.chartGrid
        },
        yAxis: {
          title: { text: 'New Hospitals', style: { color: palette.chartLabel } },
          labels: { style: { color: palette.chartLabel } },
          gridLineColor: palette.chartGrid
        },
        series: [{
          name: 'Daily Imports',
          data: dailyImports,
          color: palette.accent1,
          borderRadius: 3
        }],
        legend: { enabled: false },
        tooltip: {
          backgroundColor: palette.tooltipBg,
          borderColor: palette.tooltipBorder,
          style: { color: palette.tooltipText },
          formatter: function() { return `<b>${this.x}</b><br/>New hospitals: <b>${this.y}</b>`; }
        },
        plotOptions: {
          column: {
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              style: { color: palette.chartTitle, fontSize: '11px' }
            }
          }
        }
      });
    }

    // USA Coverage Chart
    if (coverageRef.current) {
      Highcharts.chart(coverageRef.current, {
        ...sharedChartConfig,
        chart: { type: 'spline', backgroundColor: 'transparent', height: 300 },
        title: {
          text: 'USA Hospital Coverage Progress',
          style: { color: palette.chartTitle, fontSize: '18px', fontWeight: 'bold' }
        },
        subtitle: {
          text: 'Percentage of US hospitals with pricing transparency data (6,090 total)',
          style: { color: palette.chartSubtitle }
        },
        xAxis: {
          categories: dates.map(d => new Date(d).toLocaleDateString()),
          labels: { style: { color: palette.chartLabel } },
          gridLineColor: palette.chartGrid
        },
        yAxis: {
          title: { text: 'Coverage %', style: { color: palette.chartLabel } },
          labels: {
            style: { color: palette.chartLabel },
            formatter: function() { return this.value + '%'; }
          },
          gridLineColor: palette.chartGrid,
          min: 0,
          max: Math.max(...coverageData) * 1.2
        },
        series: [{
          name: 'USA Coverage',
          data: coverageData,
          color: palette.accent4,
          lineWidth: 3,
          marker: { enabled: true, radius: 4, fillColor: palette.accent4 }
        }],
        legend: { enabled: false },
        tooltip: {
          backgroundColor: palette.tooltipBg,
          borderColor: palette.tooltipBorder,
          style: { color: palette.tooltipText },
          formatter: function() { return `<b>${this.x}</b><br/>Coverage: <b>${this.y.toFixed(2)}%</b>`; }
        }
      });
    }

  }, [analyticsData, mounted, isAHP, isDark]);

  // Loading skeleton - shown inline, not blocking the whole page
  const LoadingSkeleton = () => (
    <div className={`flex flex-col items-center justify-center py-16 ${isAHP ? (isAHPDark ? 'text-[#FAF9F6]' : 'text-[#1a3a6b]') : 'text-matrix-green'}`}>
      <div className="relative mb-6">
        {isAHP ? (
          <div className="relative">
            <Shield className={`w-16 h-16 animate-pulse ${isAHPDark ? 'text-[#5B9BD5]' : 'text-[#1a3a6b]'}`} />
            <div className="absolute inset-0 flex items-center justify-center">
              <Star className="w-6 h-6 text-[#C9A961] fill-[#C9A961] animate-spin" style={{ animationDuration: '3s' }} />
            </div>
          </div>
        ) : (
          <Activity className="w-16 h-16 text-matrix-green animate-pulse" />
        )}
      </div>
      <p className={`text-lg font-medium ${isAHP ? 'font-serif' : 'font-mono'}`}>
        Loading hospital data...
      </p>
      {isAHP && (
        <div className="mt-4 flex gap-1">
          {[
            'bg-[#B22234]',
            isAHPDark ? 'bg-[#FAF9F6] border border-[#FAF9F6]/20' : 'bg-white border border-gray-200',
            isAHPDark ? 'bg-[#5B9BD5]' : 'bg-[#1a3a6b]'
          ].map((c, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full ${c} animate-bounce`}
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={`min-h-screen ${palette.bg}`}>
      <Helmet>
        <title>Ingestion Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {!isAHP && <BackgroundMediaComponent />}

      {/* Patriotic top stripe */}
      {isAHP && (
        <div className={`w-full h-1 bg-gradient-to-r from-[#B22234] ${isAHPDark ? 'via-[#0d1e35]' : 'via-white'} to-[#1a3a6b]`} />
      )}

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            {isAHP ? (
              <>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Star className="w-8 h-8 text-[#C9A961] fill-[#C9A961]" />
                  <h1 className={`text-4xl font-bold ${palette.headerText}`} style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                    Hospital Data Collection
                  </h1>
                  <Star className="w-8 h-8 text-[#C9A961] fill-[#C9A961]" />
                </div>
                <p className={`text-lg ${palette.subText} max-w-3xl mx-auto`} style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
                  Real-time tracking of hospital pricing transparency data collection across the United States.
                  Building the most comprehensive database of medical pricing in America.
                </p>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <div className="h-0.5 w-12 bg-[#B22234]" />
                  <Shield className={`w-5 h-5 ${isAHPDark ? 'text-[#5B9BD5]' : 'text-[#1a3a6b]'}`} />
                  <div className={`h-0.5 w-12 ${isAHPDark ? 'bg-[#5B9BD5]' : 'bg-[#1a3a6b]'}`} />
                </div>
              </>
            ) : (
              <>
                <h1 className="text-4xl font-bold text-matrix-green font-mono mb-4">
                  Hospital Data Ingestion Dashboard
                </h1>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Real-time tracking of hospital pricing transparency data collection across the United States.
                  Watch as we build the most comprehensive database of medical pricing in America.
                </p>
              </>
            )}
          </div>

          {/* Hourly Ingestion Timeline */}
          <HourlyIngestionTimeline />

          {/* Loading state for main analytics */}
          {isLoading && <LoadingSkeleton />}

          {/* Key Metrics Cards */}
          {!isLoading && latestData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <Card className={isAHPDark ? 'bg-[#0d1e35] border-emerald-500/20' : isAHP ? 'bg-white/90 border-[#2b5e49]/20 shadow-sm' : 'bg-black/40 border-green-500/30'}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className={`text-sm font-medium ${palette.subText}`}>Total Hospitals</CardTitle>
                  <Database className={`h-4 w-4 ${isAHPDark ? 'text-emerald-400' : isAHP ? 'text-[#2b5e49]' : 'text-green-500'}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${isAHPDark ? 'text-emerald-400' : isAHP ? 'text-[#2b5e49]' : 'text-matrix-green'}`}>
                    {latestData.hospitals_with_data.toLocaleString()}
                  </div>
                  <p className={`text-xs ${palette.mutedText}`}>
                    With substantial pricing data
                  </p>
                </CardContent>
              </Card>

              <Card className={isAHPDark ? 'bg-[#0d1e35] border-[#5B9BD5]/20' : isAHP ? 'bg-white/90 border-[#1a3a6b]/20 shadow-sm' : 'bg-black/40 border-blue-500/30'}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className={`text-sm font-medium ${palette.subText}`}>Medical Procedures</CardTitle>
                  <BarChart3 className={`h-4 w-4 ${isAHPDark ? 'text-[#5B9BD5]' : isAHP ? 'text-[#1a3a6b]' : 'text-blue-500'}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${isAHPDark ? 'text-[#5B9BD5]' : isAHP ? 'text-[#1a3a6b]' : 'text-blue-400'}`}>
                    {(latestData.total_procedures / 1000000).toFixed(1)}M
                  </div>
                  <p className={`text-xs ${palette.mutedText}`}>
                    Procedures with pricing data
                  </p>
                </CardContent>
              </Card>

              <Card className={isAHPDark ? 'bg-[#0d1e35] border-[#B22234]/20' : isAHP ? 'bg-white/90 border-[#B22234]/20 shadow-sm' : 'bg-black/40 border-purple-500/30'}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className={`text-sm font-medium ${palette.subText}`}>USA Coverage</CardTitle>
                  <Globe className={`h-4 w-4 ${isAHPDark ? 'text-[#e57373]' : isAHP ? 'text-[#B22234]' : 'text-purple-500'}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${isAHPDark ? 'text-[#e57373]' : isAHP ? 'text-[#B22234]' : 'text-purple-400'}`}>
                    {latestData.usa_coverage_percentage.toFixed(2)}%
                  </div>
                  <p className={`text-xs ${palette.mutedText}`}>
                    Of US hospitals covered
                  </p>
                </CardContent>
              </Card>

              <Card className={isAHPDark ? 'bg-[#0d1e35] border-[#C9A961]/20' : isAHP ? 'bg-white/90 border-[#C9A961]/30 shadow-sm' : 'bg-black/40 border-orange-500/30'}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className={`text-sm font-medium ${palette.subText}`}>Today's Imports</CardTitle>
                  <TrendingUp className={`h-4 w-4 ${isAHPDark ? 'text-[#C9A961]' : isAHP ? 'text-[#C9A961]' : 'text-orange-500'}`} />
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${isAHPDark ? 'text-[#C9A961]' : isAHP ? 'text-[#C9A961]' : 'text-orange-400'}`}>
                    {latestData.new_hospitals_today}
                  </div>
                  <p className={`text-xs ${palette.mutedText}`}>
                    New hospitals added today
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Charts Grid */}
          {!isLoading && analyticsData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <Card className={palette.cardBg}>
              <CardContent className="p-6">
                <div ref={hospitalGrowthRef} />
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-6">
                <div ref={procedureGrowthRef} />
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-6">
                <div ref={dailyImportsRef} />
              </CardContent>
            </Card>

            <Card className={palette.cardBg}>
              <CardContent className="p-6">
                <div ref={coverageRef} />
              </CardContent>
            </Card>
          </div>
          )}

          {/* State Ingestion Dashboard */}
          <Card className={`${palette.cardBg} mb-12`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${palette.headerText}`}>
                <MapPin className="w-5 h-5" />
                State-by-State Ingestion Progress
              </CardTitle>
              <CardDescription className={palette.subText}>
                Monitor hospital data collection progress across all 50 US states
              </CardDescription>
            </CardHeader>
            <CardContent>
              {stateStatsLoading ? (
                <div className="text-center py-8">
                  <Activity className={`w-8 h-8 ${isAHPDark ? 'text-[#5B9BD5]' : isAHP ? 'text-[#1a3a6b]' : 'text-matrix-green'} animate-pulse mx-auto mb-4`} />
                  <p className={palette.subText}>Loading state statistics...</p>
                </div>
              ) : (
                <>
                  {/* Top States Summary */}
                  <div className="mb-6">
                    <h4 className={`text-lg font-semibold mb-4 ${palette.headerText}`}>Top States by Completion</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {stateStats?.slice(0, 6).map((state) => (
                        <Card key={state.state} className={isAHPDark ? 'bg-[#0a1628] border-[#FAF9F6]/10' : isAHP ? 'bg-[#f8f6f1] border-[#1a3a6b]/10' : 'bg-muted/20 border-muted/40'}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className={`font-semibold text-lg ${palette.headerText}`}>{state.state}</h5>
                              <Badge
                                variant={state.completion_percentage > 50 ? "default" : "secondary"}
                                className={state.completion_percentage > 50 ? palette.badgeGood : palette.badgeNeutral}
                              >
                                {state.completion_percentage.toFixed(1)}%
                              </Badge>
                            </div>
                            <div className={`space-y-1 text-sm ${palette.mutedText}`}>
                              <p>{state.hospitals_with_data}/{state.estimated_total} hospitals</p>
                              <p>{state.research_notes_count} research notes</p>
                              <p>Priority: {state.priority}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className={`w-full mt-3 ${isAHPDark ? 'border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10' : isAHP ? 'border-[#1a3a6b]/30 text-[#1a3a6b] hover:bg-[#1a3a6b]/5' : ''}`}
                              onClick={() => window.open(`/ingestion-dashboard/state/${state.state.toLowerCase()}`, '_blank')}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              View Dashboard
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* All States Grid */}
                  <div>
                    <h4 className={`text-lg font-semibold mb-4 ${palette.headerText}`}>All States Overview</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-2">
                      {stateStats?.map((state) => (
                        <Button
                          key={state.state}
                          variant="outline"
                          className={`h-16 flex flex-col gap-1 text-xs relative overflow-hidden ${
                            state.completion_percentage > 50
                              ? palette.stateGood
                              : state.completion_percentage > 20
                              ? palette.stateMid
                              : palette.stateLow
                          }`}
                          onClick={() => window.open(`/ingestion-dashboard/state/${state.state.toLowerCase()}`, '_blank')}
                        >
                          <span className={`font-bold text-sm ${palette.headerText}`}>{state.state}</span>
                          <span className="text-xs">{state.completion_percentage.toFixed(0)}%</span>
                          <span className={`text-xs ${palette.mutedText}`}>{state.hospitals_with_data}/{state.estimated_total}</span>

                          {/* Progress bar */}
                          <div className={`absolute bottom-0 left-0 h-1 ${isAHPDark ? 'bg-[#5B9BD5]' : isAHP ? 'bg-[#1a3a6b]' : 'bg-current'} opacity-30 transition-all`}
                               style={{ width: `${Math.min(state.completion_percentage, 100)}%` }} />
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* State Dashboard Features */}
                  <div className={`mt-6 p-4 rounded-lg ${palette.featureBox}`}>
                    <h4 className={`font-medium mb-2 ${palette.featureTitle}`}>State Dashboard Features:</h4>
                    <ul className={`text-sm space-y-1 ${palette.featureText}`}>
                      <li>Track estimated vs. actual hospital counts by state</li>
                      <li>View AI research summaries and compliance notes</li>
                      <li>Monitor hospital system ingestion progress</li>
                      <li>Review priority assignments and agent assignments</li>
                      <li>Access detailed research notes and next steps</li>
                    </ul>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Recent Milestones */}
          {latestData && (
            <Card className={palette.cardBg}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${palette.headerText}`}>
                  <Calendar className="w-5 h-5" />
                  Recent Milestones
                </CardTitle>
                <CardDescription className={palette.subText}>
                  Key achievements in our hospital data collection journey
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className={`flex items-center justify-between p-4 rounded-lg ${isAHPDark ? 'bg-emerald-500/5 border border-emerald-500/10' : isAHP ? 'bg-[#2b5e49]/5 border border-[#2b5e49]/10' : 'bg-muted/20'}`}>
                    <div>
                      <p className={`font-semibold ${palette.headerText}`}>80% USA Coverage Achieved</p>
                      <p className={`text-sm ${palette.mutedText}`}>
                        Now tracking {latestData.hospitals_with_data} hospitals out of 6,090 total US hospitals
                      </p>
                    </div>
                    <Badge className={palette.badgeGood}>
                      Latest
                    </Badge>
                  </div>

                  <div className={`flex items-center justify-between p-4 rounded-lg ${isAHPDark ? 'bg-[#5B9BD5]/5 border border-[#5B9BD5]/10' : isAHP ? 'bg-[#1a3a6b]/5 border border-[#1a3a6b]/10' : 'bg-muted/20'}`}>
                    <div>
                      <p className={`font-semibold ${palette.headerText}`}>250 Million Procedures Milestone</p>
                      <p className={`text-sm ${palette.mutedText}`}>
                        Comprehensive pricing data across all major medical procedures
                      </p>
                    </div>
                    <Badge variant="outline" className={isAHPDark ? 'border-[#5B9BD5]/30 text-[#5B9BD5]' : isAHP ? 'border-[#1a3a6b]/30 text-[#1a3a6b]' : ''}>Achieved</Badge>
                  </div>

                  <div className={`flex items-center justify-between p-4 rounded-lg ${isAHPDark ? 'bg-[#C9A961]/5 border border-[#C9A961]/10' : isAHP ? 'bg-[#C9A961]/5 border border-[#C9A961]/10' : 'bg-muted/20'}`}>
                    <div>
                      <p className={`font-semibold ${palette.headerText}`}>Project Launch</p>
                      <p className={`text-sm ${palette.mutedText}`}>
                        Started with 0 hospitals on September 20, 2025
                      </p>
                    </div>
                    <Badge variant="outline" className={isAHPDark ? 'border-[#C9A961]/30 text-[#C9A961]' : isAHP ? 'border-[#C9A961]/30 text-[#C9A961]' : ''}>Day 0</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Patriotic bottom accent */}
          {isAHP && (
            <div className="mt-12 text-center">
              <div className={`flex items-center justify-center gap-2 text-sm ${palette.mutedText}`}>
                <Star className="w-4 h-4 text-[#C9A961] fill-[#C9A961]" />
                <span>Price Transparency for Every American</span>
                <Star className="w-4 h-4 text-[#C9A961] fill-[#C9A961]" />
              </div>
              <div className={`mt-4 w-full h-1 bg-gradient-to-r from-[#B22234] ${isAHPDark ? 'via-[#0d1e35]' : 'via-white'} to-[#1a3a6b]`} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HospitalIngestionDashboard;
